
import React, { useState, useEffect, useMemo } from 'react';
import { Product, Category, Store, SellingModel, Promotion, FlashSale, AuctionEvent } from '../types';
import { 
  Plus, 
  Heart, 
  Star, 
  Package, 
  ChevronRight, 
  Search, 
  Loader2, 
  Store as StoreIcon, 
  ArrowRight,
  Filter as FilterIcon,
  X,
  SlidersHorizontal,
  ChevronDown,
  Camera,
  Sparkles,
  Coins,
  ExternalLink,
  ShieldCheck,
  Zap,
  ChevronLeft,
  BrainCircuit,
  Maximize,
  Hammer
} from 'lucide-react';
import { storage } from '../lib/storage';
import { Language, translations } from '../lib/i18n';
import VisualSearchModal from '../components/VisualSearchModal';
import { affiliateService } from '../services/affiliateService';
import { authService } from '../services/authService';
import { behaviorService } from '../services/behaviorService';
import { useCurrency } from '../contexts/CurrencyContext';
import { flashSaleService } from '../services/flashSaleService';
import { wishlistService } from '../services/wishlistService';
import { dynamicPricingService } from '../services/dynamicPricingService';
import { auctionService } from '../services/auctionService';
import FlashSaleBanner from '../components/FlashSaleBanner';
import LiveAuctionModal from '../components/LiveAuctionModal';

interface StoreHomeProps {
  onAddToCart: (product: Product) => void;
  onViewProduct: (product: Product) => void;
  onViewStore: (store: Store) => void;
  onBecomeSeller: () => void;
  lang: Language;
}

type SortOption = 'newest' | 'price-low' | 'price-high' | 'popular';

const StoreHome: React.FC<StoreHomeProps> = ({ onAddToCart, onViewProduct, onViewStore, onBecomeSeller, lang }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [activeSale, setActiveSale] = useState<FlashSale | null>(null);
  const [activeAuction, setActiveAuction] = useState<AuctionEvent | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [recommendations, setRecommendations] = useState<Product[]>([]);
  const [isAiRecsLoading, setIsAiRecsLoading] = useState(false);
  
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [maxPrice, setMaxPrice] = useState<number>(3000);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [showInStockOnly, setShowInStockOnly] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isVisualSearchOpen, setIsVisualSearchOpen] = useState(false);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const t = translations[lang];
  const { format, convert } = useCurrency();

  useEffect(() => {
    const loadData = async () => {
      const allProducts = storage.getProducts();
      const allCategories = storage.getCategories();
      const allStores = storage.getStores();
      const allPromos = storage.getPromotions().filter(p => p.status === 'ACTIVE').sort((a,b) => a.priority - b.priority);
      const user = await authService.getCurrentUser();
      
      const sale = flashSaleService.getActiveSale('lynqar-gadgets-001');
      const auction = auctionService.getActiveAuction();
      
      setProducts(allProducts);
      setCategories(allCategories);
      setStores(allStores);
      setPromotions(allPromos);
      setActiveSale(sale);
      setActiveAuction(auction);
      setCurrentUser(user);
      setIsLoading(false);

      if (user) {
        setIsAiRecsLoading(true);
        const recs = await behaviorService.getPersonalizedRecommendations(user.id);
        setRecommendations(recs);
        setIsAiRecsLoading(false);
      }
    };
    loadData();
    
    window.addEventListener('wishlist-update', loadData);
    window.addEventListener('behavior-update', loadData);
    window.addEventListener('auction-update', loadData);
    return () => {
      window.removeEventListener('wishlist-update', loadData);
      window.removeEventListener('behavior-update', loadData);
      window.removeEventListener('auction-update', loadData);
    };
  }, []);

  useEffect(() => {
    if (promotions.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % promotions.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [promotions]);

  const activeStoreIds = useMemo(() => {
    return stores.filter(s => s.status === 'ACTIVE').map(s => s.id);
  }, [stores]);

  const filteredProducts = useMemo(() => {
    let result = products.filter(p => {
      const isStoreActive = activeStoreIds.includes(p.storeId);
      if (!isStoreActive) return false;

      const matchesCategory = !selectedCategoryId || p.categoryId === selectedCategoryId;
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           p.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPrice = p.price <= maxPrice;
      const matchesStock = p.sellingModel === SellingModel.AFFILIATE || !showInStockOnly || p.stock > 0;
      
      return matchesCategory && matchesSearch && matchesPrice && matchesStock;
    });

    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'popular': result.sort((a, b) => ((b.wishlistCount || 0) + b.stock) - ((a.wishlistCount || 0) + a.stock)); break;
      default: result.sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime());
    }

    return result;
  }, [products, selectedCategoryId, searchQuery, maxPrice, sortBy, showInStockOnly, activeStoreIds]);

  const handleProductAction = async (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    if (product.id === activeAuction?.productId) {
      setShowAuctionModal(true);
      return;
    }
    if (product.sellingModel === SellingModel.AFFILIATE) {
      const user = await authService.getCurrentUser();
      affiliateService.trackClick(product, user);
    } else if (product.stock > 0) {
      onAddToCart(product);
    }
  };

  const handleWishlistToggle = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    const userId = currentUser?.id || 'guest-session';
    wishlistService.toggleWishlist(userId, product);
  };

  if (isLoading) {
    return (
      <div className="py-20 flex flex-col items-center justify-center space-y-4">
        <Loader2 className="h-10 w-10 text-brand-primary animate-spin" />
        <p className="text-gray-400 animate-pulse font-black uppercase tracking-widest text-[10px]">Syncing Marketplace...</p>
      </div>
    );
  }

  const auctionProduct = activeAuction ? products.find(p => p.id === activeAuction.productId) : null;

  return (
    <div className="space-y-0 pb-20 bg-gray-50/30 min-h-screen">
      {activeSale && <FlashSaleBanner sale={activeSale} />}
      
      <div className="pt-12 space-y-12">
        {/* Hero Slider Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="relative h-[600px] overflow-hidden rounded-[80px] group shadow-2xl">
            {promotions.map((promo, idx) => (
                <div 
                  key={promo.id} 
                  className={`absolute inset-0 transition-all duration-1000 ease-in-out ${idx === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-110 pointer-events-none'}`}
                >
                  <div className="absolute inset-0 bg-gray-900">
                    <img src={promo.imageUrl} alt={promo.title} className="w-full h-full object-cover opacity-50" />
                  </div>
                  <div className="relative h-full px-12 flex items-center">
                    <div className={`max-w-2xl space-y-8 transition-all duration-1000 delay-300 ${idx === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                      <div className="flex items-center gap-3">
                        <span className="bg-brand-primary text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-[0.2em] shadow-xl text-white">Platform Event</span>
                      </div>
                      <h2 className="text-6xl md:text-8xl font-black text-white tracking-tight leading-[0.85]">{promo.title}</h2>
                      <p className="text-lg text-gray-300 max-w-md font-medium leading-relaxed">{promo.subtitle}</p>
                      <div className="flex gap-4 pt-4">
                        <a href={promo.linkUrl} className="bg-white text-gray-900 px-10 py-5 rounded-[2rem] font-black text-lg hover:scale-105 transition-all shadow-2xl text-center">Explore Now</a>
                      </div>
                    </div>
                  </div>
                </div>
            ))}
           </div>
        </section>

        {/* Live Auction Highlight */}
        {activeAuction && auctionProduct && (
           <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in duration-1000">
              <div className="bg-red-600 p-10 rounded-[4rem] text-white shadow-2xl relative overflow-hidden group cursor-pointer" onClick={() => setShowAuctionModal(true)}>
                 <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors"></div>
                 <div className="absolute top-0 right-0 p-12 opacity-10 group-hover:scale-110 transition-transform"><Hammer className="h-40 w-40" /></div>
                 
                 <div className="relative flex flex-col md:flex-row justify-between items-center gap-10">
                    <div className="space-y-6">
                       <div className="flex items-center gap-4">
                          <div className="bg-white text-red-600 px-4 py-1 rounded-xl text-xs font-black uppercase tracking-widest animate-pulse">LIVE BIDDING</div>
                          <p className="text-red-100 text-[10px] font-bold uppercase tracking-[0.2em]">Competitive Acquisition Active</p>
                       </div>
                       <h2 className="text-5xl font-black tracking-tighter leading-none">Starting from {format(activeAuction.startingPrice)}</h2>
                       <p className="text-red-100 text-lg font-medium max-w-md italic">"Bhai, ye exclusive {auctionProduct.name} auction par hai. Direct buy se sasta mil sakta hai!"</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-xl border border-white/20 p-8 rounded-[3rem] text-center min-w-[240px] group-hover:bg-white/20 transition-all">
                       <p className="text-[10px] text-red-200 font-black uppercase tracking-widest mb-1">Current Highest Bid</p>
                       <h4 className="text-4xl font-black">{format(activeAuction.currentPrice)}</h4>
                       <button className="mt-6 bg-white text-red-600 px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl">Join Auction Arena</button>
                    </div>
                 </div>
              </div>
           </section>
        )}

        {/* Personalized Recommendations Section */}
        {recommendations.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in duration-1000">
             <div className="bg-gray-950 p-10 rounded-[4rem] text-white shadow-2xl relative overflow-hidden group">
                <div className="absolute inset-0 bg-blue-600/5 backdrop-blur-3xl"></div>
                <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:scale-110 transition-transform"><BrainCircuit className="h-40 w-40" /></div>
                
                <div className="relative space-y-10">
                   <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-blue-600 rounded-[1.5rem] flex items-center justify-center shadow-lg shadow-blue-500/50">
                         <Sparkles className="h-6 w-6" />
                      </div>
                      <div>
                         <h2 className="text-3xl font-black tracking-tight">Lynqar Intelligence: Picked for You</h2>
                         <p className="text-blue-400 text-[10px] font-bold uppercase tracking-widest">AI analysis of your tech taste profile</p>
                      </div>
                   </div>

                   <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {recommendations.map(p => (
                        <div 
                          key={p.id} 
                          onClick={() => onViewProduct(p)}
                          className="bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-[2.5rem] flex items-center gap-6 hover:bg-white/10 transition-all cursor-pointer group/card"
                        >
                           <div className="w-20 h-20 bg-white rounded-3xl overflow-hidden shrink-0 group-hover/card:scale-105 transition-transform">
                              <img src={p.images[0]} className="w-full h-full object-cover" />
                           </div>
                           <div className="min-w-0">
                              <p className="font-black text-white text-sm truncate">{p.name}</p>
                              <p className="text-blue-400 font-black">{format(p.price)}</p>
                              <button className="text-[8px] font-black uppercase text-gray-500 mt-2 flex items-center gap-1 group-hover/card:text-blue-300">
                                 View Magic Set <ChevronRight className="h-2.5 w-2.5" />
                              </button>
                           </div>
                        </div>
                      ))}
                   </div>
                </div>
             </div>
          </section>
        )}

        {/* Filters and Grid */}
        <div id="marketplace-grid" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <div className="flex flex-col lg:flex-row gap-12">
            <aside className={`lg:w-72 shrink-0 space-y-10 ${isSidebarOpen ? 'fixed inset-0 z-[100] bg-white p-8 overflow-y-auto' : 'hidden lg:block'}`}>
              <div className="space-y-4">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Global Search</label>
                 <div className="flex flex-col gap-3">
                    <div className="relative group">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
                      <input 
                        type="text" 
                        placeholder={t.searchPlaceholder}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-white border border-gray-100 rounded-brand shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 font-bold text-sm"
                      />
                    </div>
                    
                    <button 
                      onClick={() => setIsVisualSearchOpen(true)}
                      className="w-full bg-blue-50 text-brand-primary p-4 rounded-brand flex items-center justify-center gap-3 hover:bg-blue-100 transition-all border border-blue-100"
                    >
                      <Camera className="h-5 w-5" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Visual AI Search</span>
                    </button>
                 </div>
              </div>

              <div className="space-y-4">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Collections</label>
                 <div className="space-y-2">
                    <button onClick={() => setSelectedCategoryId(null)} className={`w-full text-left px-5 py-3 rounded-brand text-sm font-bold transition-all ${!selectedCategoryId ? 'bg-brand-primary text-white shadow-lg' : 'text-gray-500 hover:bg-gray-100'}`}>All Items</button>
                    {categories.map(cat => (
                      <button key={cat.id} onClick={() => setSelectedCategoryId(cat.id)} className={`w-full text-left px-5 py-3 rounded-brand text-sm font-bold transition-all ${selectedCategoryId === cat.id ? 'bg-brand-primary text-white shadow-lg' : 'text-gray-500 hover:bg-gray-100'}`}>{cat.name}</button>
                    ))}
                 </div>
              </div>
            </aside>

            <main className="flex-grow space-y-8">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
                 <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden flex items-center gap-2 bg-white border border-gray-200 px-6 py-3 rounded-brand text-sm font-bold shadow-sm"><SlidersHorizontal className="h-4 w-4" /> Filters</button>
                 <div className="flex items-center gap-2 bg-white p-1 rounded-brand border border-gray-100 shadow-sm">
                    {['newest', 'price-low', 'price-high', 'popular'].map((opt) => (
                      <button key={opt} onClick={() => setSortBy(opt as SortOption)} className={`px-5 py-2.5 rounded-brand text-[10px] font-black uppercase tracking-widest transition-all ${sortBy === opt ? 'bg-gray-900 text-white shadow-xl' : 'text-gray-400 hover:text-gray-700'}`}>{opt.replace('-', ' ')}</button>
                    ))}
                 </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredProducts.map((product) => {
                   const isOnSale = activeSale && activeSale.productIds.includes(product.id);
                   const isAuction = activeAuction && activeAuction.productId === product.id;
                   const storeConfig = stores.find(s => s.id === product.storeId);
                   const dpConfig = storeConfig?.dynamicPricing || { enabled: false, maxDiscountPercentage: 0, minDiscountPercentage: 0, strategy: 'BALANCED', autoApply: false };
                   const finalPrice = isOnSale 
                     ? flashSaleService.getProductDiscountedPrice(product, activeSale)
                     : dynamicPricingService.calculateAdjustedPrice(product, dpConfig);
                   const hasAiDiscount = !isOnSale && !isAuction && dpConfig.enabled && product.dynamicPriceAdjustment && product.dynamicPriceAdjustment > 0;
                   
                   return (
                    <div key={product.id} className="group bg-white rounded-[40px] border border-gray-50 overflow-hidden hover:shadow-2xl transition-all cursor-pointer p-3" onClick={() => onViewProduct(product)}>
                      <div className="relative aspect-[4/5] overflow-hidden rounded-[32px] bg-gray-100">
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                        <div className="absolute top-4 left-4 right-4 flex justify-between">
                          <button className="bg-white/90 backdrop-blur px-4 py-2 rounded-brand text-[10px] font-black uppercase tracking-widest text-gray-900 shadow-lg">
                            {storeConfig?.name || 'Store'}
                          </button>
                        </div>

                        {isOnSale && (
                          <div className="absolute top-16 left-4">
                             <span className="bg-orange-600 text-white text-[9px] font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-2xl flex items-center gap-2">
                                <Zap className="h-3 w-3 fill-current" /> FLASH SALE
                             </span>
                          </div>
                        )}

                        {isAuction && (
                          <div className="absolute top-16 left-4 animate-bounce">
                             <span className="bg-red-600 text-white text-[9px] font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-2xl flex items-center gap-2">
                                <Hammer className="h-3 w-3" /> LIVE AUCTION
                             </span>
                          </div>
                        )}

                        {hasAiDiscount && (
                           <div className="absolute top-16 left-4">
                              <span className="bg-indigo-600 text-white text-[9px] font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-2xl flex items-center gap-2">
                                 <BrainCircuit className="h-3 w-3" /> SMART PRICE
                              </span>
                           </div>
                        )}
                      </div>
                      <div className="p-6 space-y-4">
                        <h3 className="font-black text-gray-900 text-xl group-hover:text-brand-primary transition-colors leading-tight truncate">{product.name}</h3>
                        <div className="flex items-center justify-between border-t border-gray-50 pt-4">
                          <div className="flex flex-col">
                             {(isOnSale || hasAiDiscount) && <span className="text-xs font-bold text-gray-400 line-through mb-0.5">{format(product.price)}</span>}
                             <span className={`text-2xl font-black ${isAuction ? 'text-red-600' : (isOnSale || hasAiDiscount) ? 'text-indigo-600' : 'text-gray-900'}`}>
                               {isAuction ? format(activeAuction.currentPrice) : format(finalPrice)}
                             </span>
                             {isAuction && <span className="text-[8px] font-black uppercase text-red-400">Current Bid</span>}
                          </div>
                          <button 
                            onClick={(e) => handleProductAction(e, product)}
                            className={`p-4 rounded-brand transition-all ${isAuction ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-100 hover:bg-brand-primary hover:text-white'}`}
                          >
                            {isAuction ? <Hammer className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
                          </button>
                        </div>
                      </div>
                    </div>
                   );
                })}
              </div>
            </main>
          </div>
        </div>
      </div>

      {isVisualSearchOpen && (
        <VisualSearchModal onClose={() => setIsVisualSearchOpen(false)} onProductFound={(p) => onViewProduct(p)} />
      )}

      {showAuctionModal && activeAuction && auctionProduct && (
        <LiveAuctionModal 
          auction={activeAuction} 
          product={auctionProduct} 
          user={currentUser}
          onClose={() => setShowAuctionModal(false)}
          onWinner={(amount) => {
             alert(`Mubarak ho! Aapne ${format(amount)} mein ${auctionProduct.name} win kiya hai. Redirecting to checkout...`);
             onAddToCart(auctionProduct);
             setShowAuctionModal(false);
          }}
        />
      )}
    </div>
  );
};

export default StoreHome;
